import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Sparkles, X, Loader2, Send, CheckCircle2, AlertCircle, MinusCircle, ExternalLink, AlertTriangle, RefreshCcw,
} from 'lucide-react';
import { ayudaIaAPI, textoErrorApi } from '../../api/api';
import useEsMovil from '../../hooks/useEsMovil';
import useCapaAtras from '../../hooks/useCapaAtras';

// Ayuda IA en IAmail: desde un correo abierto, el usuario dice qué quiere y
// Claude (en el servidor) propone un plan de pasos. El usuario lo revisa,
// cambia los campos que quiera y lo aprueba; el ERP lo ejecuta por sus rutas
// oficiales. Nunca se envía nada (las respuestas quedan en Borradores) y
// emitir o regenerar una factura o dar de baja a un empleado pide una segunda
// confirmación.
//
// Capas: el drawer de IAmail es z-40 y el editor z-50. Este panel va en z-[60]
// para quedar por encima de los dos, y su diálogo de confirmación en z-[70].

const SONDEO_MS = 2000;
const TOPE_SONDEO_MS = 5 * 60 * 1000;
const TEXTO_TARDANDO = 'Está tardando demasiado. Vuelve a intentarlo.';
const AVISO_LENTO_MS = 90 * 1000;

// Barra fina indeterminada del progreso: un tramo de color de marca que se desplaza
const ESTILO_BARRA = `
@keyframes ayudaIaBarra { 0% { transform: translateX(-100%); } 100% { transform: translateX(300%); } }
.ayuda-ia-barra { animation: ayudaIaBarra 1.4s ease-in-out infinite; }
@media (prefers-reduced-motion: reduce) { .ayuda-ia-barra { animation: none; } }
`;
// El servidor pide la segunda confirmación con 409 y un texto que empieza así
// (factura: 'Confirma la emisión...'; baja: 'Confirma la baja del empleado.')
const CONFIRMAR_409 = 'Confirma la ';

const SUGERENCIAS = [
  'Haz lo que pide el correo',
  'Prepara la respuesta',
  '¿A quién hay que facturar?',
];

const errorDe = (e, porDefecto) => textoErrorApi(e?.response?.data?.detail, porDefecto);

const formatoCoste = (n) => Number(n || 0).toLocaleString('es-ES', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

// Topes y crédito mensual: "5" o "100" si son enteros, si no con 2 decimales
const formatoLimite = (n) => {
  const v = Number(n || 0);
  return Number.isInteger(v) ? v.toLocaleString('es-ES') : formatoCoste(v);
};

// Color de la barrita del crédito usado: verde < 70 %, ámbar 70-90 %, rojo > 90 %
const colorCredito = (pct) => {
  const v = Number(pct) || 0;
  if (v > 90) return 'bg-red-500';
  if (v >= 70) return 'bg-amber-500';
  return 'bg-emerald-500';
};

// Valor de un parámetro para mostrarlo en un campo editable
const valorParaCampo = (v) => {
  if (v === null || v === undefined) return '';
  if (Array.isArray(v)) return v.join(', ');
  if (typeof v === 'object') {
    try { return JSON.stringify(v); } catch (_e) { return ''; }
  }
  return String(v);
};

// Vuelve a dar al valor editado el tipo que tenía el parámetro original
const valorDesdeCampo = (texto, original, tipo) => {
  const t = texto ?? '';
  if (Array.isArray(original)) {
    return t.split(',').map((x) => x.trim()).filter(Boolean);
  }
  if (tipo === 'numero' || typeof original === 'number') {
    const limpio = String(t).trim();
    if (!limpio) return null;
    const n = Number(limpio.replace(',', '.'));
    return Number.isNaN(n) ? limpio : n;
  }
  return t;
};

// Estado editable de un plan: { [pasoId]: { aprobado, valores: { clave: texto } } }
const edicionInicial = (plan) => {
  const res = {};
  (plan?.pasos || []).forEach((p) => {
    const valores = {};
    (p.campos_editables || []).forEach((c) => {
      valores[c.clave] = valorParaCampo(p.parametros?.[c.clave]);
    });
    res[p.id] = { aprobado: true, valores };
  });
  return res;
};

// Paso al que apunta una referencia "$p1..." (o null si no lo es)
const pasoReferido = (v) => {
  if (typeof v !== 'string' || !v.startsWith('$')) return null;
  const m = v.match(/^\$\{?([A-Za-z0-9_-]+)/);
  return m ? m[1] : null;
};

// "12 s", "1 min 05 s"
const formatoTranscurrido = (ms) => {
  const total = Math.max(0, Math.floor((Number(ms) || 0) / 1000));
  if (total < 60) return `${total} s`;
  const minutos = Math.floor(total / 60);
  const segundos = total % 60;
  return `${minutos} min ${String(segundos).padStart(2, '0')} s`;
};

const ordenarPorFecha = (lista) => [...lista].sort((a, b) => {
  const fa = String(a.actualizada_en || a.creada_en || '');
  const fb = String(b.actualizada_en || b.creada_en || '');
  return fb.localeCompare(fa);
});

const CampoEditable = ({ campo, valor, onChange, esMovil, deshabilitado }) => {
  const clase = `w-full rounded-md border border-gray-300 bg-white px-2.5 py-1.5 ${esMovil ? 'text-base' : 'text-sm'} text-gray-900 focus:outline-none focus:ring-2 focus:ring-violet-300 disabled:bg-gray-50`;
  const tipoInput = campo.tipo === 'email' ? 'email'
    : campo.tipo === 'numero' ? 'number'
      : campo.tipo === 'fecha' ? 'date'
        : 'text';
  return (
    <label className="block">
      <span className="block text-xs font-medium text-gray-600 mb-1">{campo.etiqueta || campo.clave}</span>
      {campo.tipo === 'texto_largo' ? (
        <textarea
          rows={4}
          value={valor}
          onChange={(e) => onChange(e.target.value)}
          disabled={deshabilitado}
          className={`${clase} resize-y`}
        />
      ) : (
        <input
          type={tipoInput}
          value={valor}
          onChange={(e) => onChange(e.target.value)}
          disabled={deshabilitado}
          step={campo.tipo === 'numero' ? 'any' : undefined}
          className={clase}
        />
      )}
    </label>
  );
};

const Burbuja = ({ mensaje }) => {
  if (mensaje.rol === 'sistema') {
    return (
      <div className="flex justify-center">
        <div className="max-w-[90%] text-center text-xs text-gray-500 px-2 py-1 whitespace-pre-wrap break-words">
          {mensaje.texto}
        </div>
      </div>
    );
  }
  const esUsuario = mensaje.rol === 'usuario';
  return (
    <div className={`flex ${esUsuario ? 'justify-end' : 'justify-start'}`}>
      <div
        className={`max-w-[85%] rounded-2xl px-3 py-2 text-sm whitespace-pre-wrap break-words ${
          esUsuario
            ? 'bg-gray-100 text-gray-900 rounded-br-sm'
            : 'bg-white text-gray-800 border border-gray-200 rounded-bl-sm'
        }`}
      >
        {mensaje.texto}
      </div>
    </div>
  );
};

const AyudaIAPanel = ({ abierto, onCerrar, correo, onCambios }) => {
  const navigate = useNavigate();
  const esMovil = useEsMovil();
  const [sesion, setSesion] = useState(null);
  const [cargandoInicial, setCargandoInicial] = useState(false);
  const [texto, setTexto] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [errorLocal, setErrorLocal] = useState(null);
  const [tardando, setTardando] = useState(false);
  const [edicion, setEdicion] = useState({});
  const [ejecutando, setEjecutando] = useState(false);
  const [confirmando, setConfirmando] = useState(false); // diálogo de factura abierto
  const [estadoIa, setEstadoIa] = useState(null); // /ayuda-ia/estado: gasto de hoy, tope y crédito del mes

  const cuerpoRef = useRef(null);
  const campoRef = useRef(null);
  const sesionIdRef = useRef(null);
  const inicioSondeoRef = useRef(0);
  const planEditadoRef = useRef(null); // id del plan cuya edición está cargada
  const onCambiosRef = useRef(onCambios);
  onCambiosRef.current = onCambios;

  const correoId = correo?.id || null;

  // Móvil: el botón atrás cierra el panel
  useCapaAtras('ayuda-ia', esMovil && abierto, onCerrar);

  const ponerSesion = useCallback((s) => {
    sesionIdRef.current = s?.id || null;
    setSesion(s || null);
  }, []);

  // Al abrir (o cambiar de correo): se carga la última conversación del correo
  useEffect(() => {
    if (!abierto || !correoId) return undefined;
    let cancelado = false;
    ponerSesion(null);
    setTexto('');
    setErrorLocal(null);
    setTardando(false);
    setEdicion({});
    setConfirmando(false);
    planEditadoRef.current = null;
    setCargandoInicial(true);
    (async () => {
      try {
        const { data } = await ayudaIaAPI.listar(correoId);
        const lista = Array.isArray(data) ? data : [];
        if (cancelado || lista.length === 0) return;
        const ultima = ordenarPorFecha(lista)[0];
        const { data: completa } = await ayudaIaAPI.obtener(ultima.id);
        if (cancelado) return;
        inicioSondeoRef.current = Date.now();
        ponerSesion(completa);
      } catch (e) {
        if (!cancelado) setErrorLocal(errorDe(e, 'No se pudo cargar la conversación.'));
      } finally {
        if (!cancelado) setCargandoInicial(false);
      }
    })();
    return () => { cancelado = true; };
  }, [abierto, correoId, ponerSesion]);

  // Sondeo cada 2 s mientras la IA piensa (se para al cerrar o desmontar; tope 5 min).
  // Cada respuesta se pinta (trae el progreso); al cerrar el panel el turno sigue
  // en el servidor y, al volver a abrirlo, se carga la sesión y se reanuda el sondeo.
  const sesionId = sesion?.id || null;
  const pensando = sesion?.estado === 'pensando';
  useEffect(() => {
    if (!abierto || !sesionId || !pensando || tardando) return undefined;
    if (!inicioSondeoRef.current) inicioSondeoRef.current = Date.now();
    let cancelado = false;
    const intervalo = setInterval(async () => {
      if (Date.now() - inicioSondeoRef.current > TOPE_SONDEO_MS) {
        clearInterval(intervalo);
        if (!cancelado) setTardando(true);
        return;
      }
      try {
        const { data } = await ayudaIaAPI.obtener(sesionId);
        if (cancelado || sesionIdRef.current !== sesionId) return;
        if (data) ponerSesion(data);
      } catch (_e) {
        // Se reintenta en la siguiente vuelta
      }
    }, SONDEO_MS);
    return () => {
      cancelado = true;
      clearInterval(intervalo);
    };
  }, [abierto, sesionId, pensando, tardando, ponerSesion]);

  // Gasto de hoy y crédito del mes (estimado): al abrir el panel y al terminar
  // cada turno o ejecución
  const refrescarEstado = useCallback(async () => {
    try {
      const { data } = await ayudaIaAPI.estado();
      setEstadoIa(data || null);
    } catch (_e) {
      // El pie se queda con lo último que se leyó
    }
  }, []);
  useEffect(() => {
    if (abierto) refrescarEstado();
  }, [abierto, refrescarEstado]);
  const pensabaRef = useRef(false);
  useEffect(() => {
    if (abierto && pensabaRef.current && !pensando) refrescarEstado();
    pensabaRef.current = pensando;
  }, [abierto, pensando, refrescarEstado]);

  // Reloj del tiempo transcurrido del turno (cada segundo, solo mientras piensa)
  const [ahoraMs, setAhoraMs] = useState(() => Date.now());
  useEffect(() => {
    if (!abierto || !pensando) return undefined;
    setAhoraMs(Date.now());
    const reloj = setInterval(() => setAhoraMs(Date.now()), 1000);
    return () => clearInterval(reloj);
  }, [abierto, pensando]);

  // Edición del plan: se prepara cada vez que llega un plan nuevo
  const plan = sesion?.plan || null;
  const planId = plan?.id || null;
  useEffect(() => {
    if (!plan) {
      planEditadoRef.current = null;
      setEdicion({});
      return;
    }
    if (planEditadoRef.current === plan.id) return;
    planEditadoRef.current = plan.id;
    setEdicion(edicionInicial(plan));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [planId]);

  // Al llegar mensajes o el plan, se baja al final
  const numMensajes = sesion?.conversacion?.length || 0;
  const estadoSesion = sesion?.estado || null;
  const estadoPlan = plan?.estado || null;
  const numProgreso = sesion?.progreso?.length || 0;
  useEffect(() => {
    const el = cuerpoRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [numMensajes, estadoSesion, planId, estadoPlan, numProgreso, abierto]);

  // El campo crece de 2 a 6 filas
  useEffect(() => {
    const el = campoRef.current;
    if (!el) return;
    el.style.height = 'auto';
    const alturaLinea = parseFloat(window.getComputedStyle(el).lineHeight) || 20;
    const relleno = 16;
    const maximo = alturaLinea * 6 + relleno;
    el.style.height = `${Math.min(el.scrollHeight, maximo)}px`;
    el.style.overflowY = el.scrollHeight > maximo ? 'auto' : 'hidden';
  }, [texto, abierto]);

  // Escritorio: Escape cierra el panel (o el diálogo de la factura si está abierto)
  useEffect(() => {
    if (!abierto || esMovil) return undefined;
    const alPulsar = (ev) => {
      if (ev.key !== 'Escape') return;
      if (confirmando) {
        if (!ejecutando) setConfirmando(false);
        return;
      }
      onCerrar?.();
    };
    window.addEventListener('keydown', alPulsar);
    return () => window.removeEventListener('keydown', alPulsar);
  }, [abierto, esMovil, confirmando, ejecutando, onCerrar]);

  const enviarTexto = useCallback(async (contenido) => {
    const limpio = String(contenido || '').trim();
    if (!limpio || !correoId || enviando) return false;
    setEnviando(true);
    setErrorLocal(null);
    setTardando(false);
    try {
      const { data } = sesionIdRef.current
        ? await ayudaIaAPI.mensaje(sesionIdRef.current, limpio)
        : await ayudaIaAPI.crear(correoId, limpio);
      inicioSondeoRef.current = Date.now();
      ponerSesion(data);
      return true;
    } catch (e) {
      setErrorLocal(errorDe(e, 'No se pudo enviar el mensaje.'));
      return false;
    } finally {
      setEnviando(false);
    }
  }, [correoId, enviando, ponerSesion]);

  const enviar = async () => {
    const ok = await enviarTexto(texto);
    if (ok) setTexto('');
  };

  const ultimoMensajeUsuario = () => {
    const conv = sesion?.conversacion || [];
    for (let i = conv.length - 1; i >= 0; i -= 1) {
      if (conv[i]?.rol === 'usuario' && conv[i]?.texto) return conv[i].texto;
    }
    return '';
  };

  const reintentar = async () => {
    if (tardando && sesionIdRef.current) {
      // La IA puede seguir trabajando: se mira cómo está y, si sigue, se vuelve a esperar
      setTardando(false);
      setErrorLocal(null);
      inicioSondeoRef.current = Date.now();
      try {
        const { data } = await ayudaIaAPI.obtener(sesionIdRef.current);
        ponerSesion(data);
      } catch (e) {
        setErrorLocal(errorDe(e, 'No se pudo consultar la conversación.'));
      }
      return;
    }
    await enviarTexto(ultimoMensajeUsuario());
  };

  const nuevaConversacion = () => {
    ponerSesion(null);
    setTexto('');
    setErrorLocal(null);
    setTardando(false);
    setEdicion({});
    planEditadoRef.current = null;
    setTimeout(() => campoRef.current?.focus(), 0);
  };

  const cambiarAprobado = (pasoId, aprobado) => {
    setEdicion((prev) => ({ ...prev, [pasoId]: { ...(prev[pasoId] || { valores: {} }), aprobado } }));
  };

  const cambiarValor = (pasoId, clave, valor) => {
    setEdicion((prev) => {
      const actual = prev[pasoId] || { aprobado: true, valores: {} };
      return { ...prev, [pasoId]: { ...actual, valores: { ...actual.valores, [clave]: valor } } };
    });
  };

  const pasosPlan = plan?.pasos || [];
  const estaAprobado = (p) => edicion[p.id]?.aprobado !== false;
  const aprobados = pasosPlan.filter(estaAprobado);
  const aprobadosConFactura = aprobados.filter((p) => p.requiere_confirmacion);
  const hayBaja = aprobadosConFactura.some((p) => p.tipo_confirmacion === 'baja');
  const hayFactura = aprobadosConFactura.some((p) => p.tipo_confirmacion !== 'baja');
  const tituloConfirmacion = hayBaja && hayFactura
    ? 'Vas a dar de baja a un empleado y a emitir o regenerar una factura'
    : hayBaja ? 'Vas a dar de baja a un empleado' : 'Vas a emitir o regenerar una factura';

  const cuerpoEjecutar = (confirmarFacturas) => ({
    plan_id: plan?.id,
    confirmar_facturas: !!confirmarFacturas,
    pasos: pasosPlan.map((p) => {
      const ed = edicion[p.id] || { aprobado: true, valores: {} };
      const parametros = { ...(p.parametros || {}) };
      (p.campos_editables || []).forEach((c) => {
        if (Object.prototype.hasOwnProperty.call(ed.valores || {}, c.clave)) {
          parametros[c.clave] = valorDesdeCampo(ed.valores[c.clave], p.parametros?.[c.clave], c.tipo);
        }
      });
      return { id: p.id, aprobado: ed.aprobado !== false, parametros };
    }),
  });

  const ejecutar = async (confirmarFacturas = false) => {
    if (!sesionIdRef.current || !plan || ejecutando || aprobados.length === 0) return;
    if (aprobadosConFactura.length > 0 && !confirmarFacturas) {
      setConfirmando(true);
      return;
    }
    setEjecutando(true);
    setErrorLocal(null);
    try {
      const { data } = await ayudaIaAPI.ejecutar(sesionIdRef.current, cuerpoEjecutar(confirmarFacturas));
      setConfirmando(false);
      inicioSondeoRef.current = Date.now();
      ponerSesion(data);
      onCambiosRef.current?.();
      refrescarEstado();
    } catch (e) {
      const detalle = errorDe(e, 'No se pudo ejecutar el plan.');
      if (e?.response?.status === 409 && String(detalle).startsWith(CONFIRMAR_409)) {
        setConfirmando(true);
      } else {
        setConfirmando(false);
        setErrorLocal(detalle);
      }
    } finally {
      setEjecutando(false);
    }
  };

  const abrirEnlace = (enlace) => {
    if (!enlace || !String(enlace).startsWith('/')) return;
    if (esMovil) {
      // Como irA de Emails.js: con capas abiertas se sustituye su entrada del
      // historial, y al volver con atrás se llega a /emails
      const enCapa = Array.isArray(window.history.state?.duettoCapas) && window.history.state.duettoCapas.length > 0;
      navigate(enlace, enCapa ? { replace: true } : undefined);
      onCerrar?.();
    } else {
      window.open(enlace, '_blank', 'noopener');
    }
  };

  const alPulsarTecla = (ev) => {
    if (ev.key === 'Enter' && (ev.ctrlKey || ev.metaKey)) {
      ev.preventDefault();
      enviar();
    }
  };

  if (!abierto || !correo) return null;

  const conversacion = sesion?.conversacion || [];
  const vacio = !sesion && !cargandoInicial;
  const campoDeshabilitado = enviando || pensando || ejecutando || cargandoInicial;
  const asunto = correo.asunto || '(sin asunto)';
  const remitente = correo.remitente_nombre || correo.remitente || '';
  const progreso = Array.isArray(sesion?.progreso) ? sesion.progreso : [];
  const inicioTurnoMs = Date.parse(sesion?.turno_iniciado_en || '');
  const transcurridoMs = Math.max(0, ahoraMs - (Number.isNaN(inicioTurnoMs) ? (inicioSondeoRef.current || ahoraMs) : inicioTurnoMs));
  const numeroPaso = (id) => {
    const i = pasosPlan.findIndex((p) => p.id === id);
    return i >= 0 ? String(i + 1) : id;
  };
  const credito = estadoIa?.credito || null;
  const avisoCredito = estadoIa?.aviso_credito || null;
  const hayGastoHoy = estadoIa && estadoIa.gasto_hoy !== undefined && estadoIa.gasto_hoy !== null;
  const pctCredito = credito ? Math.min(100, Math.max(0, Number(credito.porcentaje_usado) || 0)) : 0;

  const contenedor = esMovil
    ? 'fixed inset-0 z-[60] bg-white flex flex-col h-screen h-dvh'
    : 'fixed inset-y-0 right-0 w-[480px] max-w-full z-[60] bg-white shadow-2xl border-l flex flex-col';

  const renderPasoPropuesto = (p, i) => {
    const ed = edicion[p.id] || { aprobado: true, valores: {} };
    const aprobado = ed.aprobado !== false;
    const editables = p.campos_editables || [];
    const clavesEditables = new Set(editables.map((c) => c.clave));
    const referencias = Object.entries(p.parametros || {})
      .filter(([clave]) => !clavesEditables.has(clave))
      .flatMap(([clave, v]) => {
        const valores = Array.isArray(v) ? v : [v];
        return valores.map((x) => pasoReferido(x)).filter(Boolean).map((ref) => ({ clave, ref }));
      });
    return (
      <li key={p.id} className={`rounded-lg border p-3 ${aprobado ? 'border-gray-200 bg-white' : 'border-gray-200 bg-gray-50 opacity-70'}`}>
        <label className="flex items-start gap-2.5 cursor-pointer">
          <input
            type="checkbox"
            checked={aprobado}
            onChange={(e) => cambiarAprobado(p.id, e.target.checked)}
            disabled={ejecutando}
            className="mt-0.5 h-5 w-5 shrink-0 accent-violet-600"
          />
          <span className="min-w-0 flex-1">
            <span className="block text-sm font-semibold text-gray-900">
              {i + 1}. {p.titulo}
            </span>
            {p.explicacion && <span className="block text-xs text-gray-500 mt-0.5 whitespace-pre-wrap">{p.explicacion}</span>}
          </span>
        </label>
        {p.requiere_confirmacion && (
          <span className="inline-flex items-center gap-1 mt-2 ml-7 rounded-full bg-red-50 border border-red-200 px-2 py-0.5 text-[11px] font-medium text-red-700">
            <AlertTriangle className="w-3 h-3" /> {p.tipo_confirmacion === 'baja' ? 'Baja' : 'Factura'}: te pediré confirmación
          </span>
        )}
        {referencias.length > 0 && (
          <div className="mt-2 ml-7 space-y-0.5">
            {referencias.map((r) => (
              <div key={`${r.clave}-${r.ref}`} className="text-[11px] text-gray-500">
                {r.clave}: usa el resultado del paso {numeroPaso(r.ref)}
              </div>
            ))}
          </div>
        )}
        {editables.length > 0 && aprobado && (
          <div className="mt-3 ml-7 space-y-2.5">
            {editables.map((c) => (
              <CampoEditable
                key={c.clave}
                campo={c}
                valor={ed.valores?.[c.clave] ?? ''}
                onChange={(v) => cambiarValor(p.id, c.clave, v)}
                esMovil={esMovil}
                deshabilitado={ejecutando}
              />
            ))}
          </div>
        )}
      </li>
    );
  };

  const renderPasoEjecutado = (p, i) => {
    const hecho = p.estado === 'hecho';
    const fallo = p.estado === 'error';
    const Icono = hecho ? CheckCircle2 : fallo ? AlertCircle : MinusCircle;
    const color = hecho ? 'text-emerald-600' : fallo ? 'text-red-600' : 'text-gray-400';
    const enlace = p.resultado?.enlace;
    return (
      <li key={p.id} className={`rounded-lg border p-3 ${hecho ? 'border-emerald-200 bg-emerald-50/50' : fallo ? 'border-red-200 bg-red-50/50' : 'border-gray-200 bg-gray-50'}`}>
        <div className="flex items-start gap-2.5">
          <Icono className={`w-5 h-5 shrink-0 mt-0.5 ${color}`} />
          <div className="min-w-0 flex-1">
            <div className={`text-sm font-semibold ${p.estado === 'omitido' ? 'text-gray-500' : 'text-gray-900'}`}>
              {i + 1}. {p.titulo}
            </div>
            <div className={`text-xs mt-0.5 ${hecho ? 'text-emerald-700' : fallo ? 'text-red-700' : 'text-gray-500'}`}>
              {hecho ? 'Hecho' : fallo ? 'Error' : p.estado === 'omitido' ? 'Omitido' : 'Sin ejecutar'}
            </div>
            {p.resultado?.texto && (
              <div className={`text-xs mt-1 whitespace-pre-wrap break-words ${fallo ? 'text-red-700' : 'text-gray-700'}`}>
                {p.resultado.texto}
              </div>
            )}
            {enlace && String(enlace).startsWith('/') && (
              <button
                type="button"
                onClick={() => abrirEnlace(enlace)}
                className={`mt-2 inline-flex items-center gap-1 rounded-md border border-violet-300 px-2.5 text-xs font-medium text-violet-700 hover:bg-violet-50 ${esMovil ? 'min-h-[44px]' : 'py-1'}`}
              >
                <ExternalLink className="w-3.5 h-3.5" /> Abrir
              </button>
            )}
          </div>
        </div>
      </li>
    );
  };

  return (
    <>
      <div className={contenedor} data-guia="ayuda-ia-panel" role="dialog" aria-label="Ayuda IA">
        {/* Cabecera */}
        <div className="shrink-0 flex items-center gap-2 border-b bg-white px-3 py-2" style={esMovil ? { paddingTop: 'calc(env(safe-area-inset-top) + 8px)' } : undefined}>
          <Sparkles className="w-5 h-5 text-violet-600 shrink-0" />
          <div className="min-w-0 flex-1">
            <div className="text-sm font-semibold text-gray-900">Ayuda IA</div>
            <div className="text-xs text-gray-500 truncate" title={asunto}>{asunto}</div>
          </div>
          {sesion && !pensando && !ejecutando && (
            <button
              type="button"
              onClick={nuevaConversacion}
              className={`text-xs text-violet-700 hover:underline shrink-0 ${esMovil ? 'min-h-[44px] px-2' : 'px-1'}`}
            >
              Nueva conversación
            </button>
          )}
          <button
            type="button"
            onClick={() => onCerrar?.()}
            className={`shrink-0 inline-flex items-center justify-center rounded-lg text-gray-500 hover:bg-gray-100 ${esMovil ? 'w-11 h-11' : 'w-8 h-8'}`}
            aria-label="Cerrar Ayuda IA"
            title="Cerrar"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Cuerpo con scroll */}
        <div ref={cuerpoRef} className="flex-1 min-h-0 overflow-y-auto bg-gray-50/60 px-3 py-3 space-y-3">
          {cargandoInicial && (
            <div className="flex items-center justify-center gap-2 py-8 text-sm text-gray-500">
              <Loader2 className="w-4 h-4 animate-spin" /> Cargando...
            </div>
          )}

          {vacio && (
            <div className="py-4">
              <p className="text-sm text-gray-600">
                Dime qué quieres hacer con este correo. Te propondré los pasos y no se hará nada hasta que los apruebes.
              </p>
              {remitente && <p className="mt-1 text-xs text-gray-400 truncate">De: {remitente}</p>}
              <div className="mt-3 flex flex-wrap gap-2">
                {SUGERENCIAS.map((s) => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => {
                      setTexto(s);
                      setTimeout(() => campoRef.current?.focus(), 0);
                    }}
                    data-uso="ayuda_ia.sugerencia"
                    data-uso-detalle={s}
                    className={`rounded-full border border-violet-200 bg-white px-3 text-sm text-violet-700 hover:bg-violet-50 ${esMovil ? 'min-h-[40px]' : 'py-1'}`}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* La conversación solo crece: posición y fecha son una clave estable */}
          {conversacion.map((m, i) => (
            <Burbuja key={`${i}-${m.fecha || ''}`} mensaje={m} />
          ))}

          {plan && (
            <div className="rounded-xl border border-violet-200 bg-white shadow-sm" data-guia="ayuda-ia-plan">
              <div className="px-3 pt-3">
                <div className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-violet-700">
                  <Sparkles className="w-3.5 h-3.5" />
                  {plan.estado === 'ejecutado' ? 'Plan ejecutado' : 'Plan propuesto'}
                </div>
                {plan.resumen && <p className="mt-1 text-sm text-gray-800 whitespace-pre-wrap">{plan.resumen}</p>}
                {(plan.avisos || []).length > 0 && (
                  <div className="mt-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 space-y-1">
                    {plan.avisos.map((a) => (
                      <div key={a} className="flex items-start gap-1.5 text-xs text-amber-800">
                        <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                        <span className="whitespace-pre-wrap">{a}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <ol className="px-3 py-3 space-y-2">
                {pasosPlan.map((p, i) => (plan.estado === 'ejecutado' ? renderPasoEjecutado(p, i) : renderPasoPropuesto(p, i)))}
              </ol>
              {plan.estado !== 'ejecutado' && (
                <div className="sticky bottom-0 rounded-b-xl border-t bg-white px-3 py-2.5">
                  <button
                    type="button"
                    onClick={() => ejecutar(false)}
                    disabled={ejecutando || pensando || aprobados.length === 0}
                    data-uso="ayuda_ia.ejecutar"
                    className={`w-full inline-flex items-center justify-center gap-2 rounded-lg bg-violet-600 px-4 text-sm font-medium text-white hover:bg-violet-700 disabled:opacity-50 ${esMovil ? 'min-h-[48px]' : 'py-2'}`}
                  >
                    {ejecutando ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" /> Ejecutando...
                      </>
                    ) : (
                      `Ejecutar ${aprobados.length} ${aprobados.length === 1 ? 'paso' : 'pasos'}`
                    )}
                  </button>
                </div>
              )}
            </div>
          )}

          {pensando && !tardando && (
            <div
              className="rounded-xl border border-gray-200 bg-white shadow-sm"
              data-guia="ayuda-ia-progreso"
              role="status"
              aria-live="polite"
            >
              <style>{ESTILO_BARRA}</style>
              <div className="flex items-center gap-3 px-3 pt-3">
                <div className="relative h-1 flex-1 overflow-hidden rounded-full bg-duetto-primary/15">
                  <div className="ayuda-ia-barra absolute inset-y-0 left-0 w-1/3 rounded-full bg-duetto-primary" />
                </div>
                <span className="shrink-0 text-xs tabular-nums text-gray-500">{formatoTranscurrido(transcurridoMs)}</span>
              </div>
              <ul className="px-3 pt-2.5 pb-2 space-y-1.5">
                {progreso.length === 0 ? (
                  <li className="flex items-center gap-2 text-sm font-semibold text-gray-900">
                    <Loader2 className="w-4 h-4 shrink-0 animate-spin text-duetto-primary" />
                    Empezando...
                  </li>
                ) : progreso.map((e, i) => (
                  // El progreso del turno solo crece: fecha y posición son una clave estable
                  <li
                    key={`${e.fecha || ''}-${i}`}
                    className={`flex items-start gap-2 text-sm ${e.hecho ? 'text-gray-700' : 'font-semibold text-gray-900'}`}
                  >
                    {e.hecho
                      ? <CheckCircle2 className="w-4 h-4 shrink-0 mt-0.5 text-emerald-600" />
                      : <Loader2 className="w-4 h-4 shrink-0 mt-0.5 animate-spin text-duetto-primary" />}
                    <span className="min-w-0 break-words">{e.texto}</span>
                  </li>
                ))}
              </ul>
              <div className="px-3 pb-3 text-[11px] text-gray-500">
                Puedes cerrar el panel: seguirá trabajando y lo verás al volver.
              </div>
              {transcurridoMs > AVISO_LENTO_MS && (
                <div className="px-3 pb-3 -mt-2 text-xs text-amber-700">Está tardando más de lo normal.</div>
              )}
            </div>
          )}

          {(sesion?.estado === 'error' || tardando) && (
            <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2.5">
              <div className="flex items-start gap-2 text-sm text-red-700">
                <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
                <span className="whitespace-pre-wrap break-words">
                  {tardando ? TEXTO_TARDANDO : (sesion?.error || 'Algo ha fallado.')}
                </span>
              </div>
              <button
                type="button"
                onClick={reintentar}
                disabled={enviando}
                className={`mt-2 inline-flex items-center gap-1.5 rounded-md border border-red-300 bg-white px-3 text-xs font-medium text-red-700 hover:bg-red-100 disabled:opacity-50 ${esMovil ? 'min-h-[44px]' : 'py-1.5'}`}
              >
                {enviando ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCcw className="w-3.5 h-3.5" />}
                Reintentar
              </button>
            </div>
          )}

          {errorLocal && (
            <div className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
              <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
              <span className="whitespace-pre-wrap break-words">{errorLocal}</span>
            </div>
          )}
        </div>

        {/* Pie fijo */}
        <div
          className="shrink-0 border-t bg-white px-3 pt-2 pb-2"
          style={esMovil ? { paddingBottom: 'calc(env(safe-area-inset-bottom) + 8px)' } : undefined}
        >
          {avisoCredito && (
            <div className="mb-2 flex items-start gap-1.5 rounded-lg border border-amber-200 bg-amber-50 px-2.5 py-1.5 text-xs text-amber-800" role="status">
              <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
              <span>{avisoCredito}</span>
            </div>
          )}
          <div className="flex items-end gap-2">
            <textarea
              ref={campoRef}
              rows={2}
              value={texto}
              onChange={(e) => setTexto(e.target.value)}
              onKeyDown={alPulsarTecla}
              disabled={campoDeshabilitado}
              placeholder={sesion ? 'Escribe para seguir o cambiar el plan' : 'Qué quieres hacer con este correo'}
              className={`flex-1 resize-none rounded-lg border border-gray-300 bg-white px-3 py-2 ${esMovil ? 'text-base' : 'text-sm'} text-gray-900 focus:outline-none focus:ring-2 focus:ring-violet-300 disabled:bg-gray-50`}
            />
            <button
              type="button"
              onClick={enviar}
              disabled={campoDeshabilitado || !texto.trim()}
              data-uso="ayuda_ia.enviar"
              className={`shrink-0 inline-flex items-center justify-center gap-1.5 rounded-lg bg-violet-600 text-sm font-medium text-white hover:bg-violet-700 disabled:opacity-50 ${esMovil ? 'w-12 h-12' : 'px-3 py-2'}`}
              title="Enviar (Ctrl o Cmd + Enter)"
              aria-label="Enviar"
            >
              {enviando ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              {!esMovil && <span>Enviar</span>}
            </button>
          </div>
          {(sesion || hayGastoHoy || credito) && (
            <div className="mt-1" data-testid="ayuda-ia-gasto">
              <div className="flex flex-wrap items-center gap-x-1.5 text-[11px] text-gray-400 tabular-nums">
                {sesion && <span>Esta conversación: {formatoCoste(sesion.coste_usd)} $</span>}
                {sesion && (hayGastoHoy || credito) && <span aria-hidden="true">·</span>}
                {hayGastoHoy && (
                  <span>Hoy: {formatoCoste(estadoIa.gasto_hoy)} $ de {formatoLimite(estadoIa.tope_dia)} $</span>
                )}
                {hayGastoHoy && credito && <span aria-hidden="true">·</span>}
                {credito && (
                  <span
                    className="cursor-help"
                    title={`Estimado por el ERP con lo que ha gastado Ayuda IA desde el día ${credito.dia_renovacion}. El saldo exacto está en platform.claude.com > Billing.`}
                  >
                    Crédito del mes: quedan {formatoCoste(credito.restante)} $ de {formatoLimite(credito.mensual)} $
                  </span>
                )}
              </div>
              {credito && (
                <div
                  className="mt-1 h-1 w-full overflow-hidden rounded-full bg-gray-100"
                  role="progressbar"
                  aria-label="Crédito del mes usado (estimado)"
                  aria-valuemin={0}
                  aria-valuemax={100}
                  aria-valuenow={Math.round(pctCredito)}
                >
                  <div className={`h-full rounded-full ${colorCredito(pctCredito)}`} style={{ width: `${pctCredito}%` }} />
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Segunda confirmación para emitir o regenerar una factura o dar de baja a un empleado */}
      {confirmando && (
        <div
          className="fixed inset-0 z-[70] bg-black/40 flex items-center justify-center p-4"
          onClick={() => !ejecutando && setConfirmando(false)}
        >
          <div
            className="w-full max-w-md rounded-xl bg-white shadow-2xl p-5"
            role="dialog"
            aria-modal="true"
            aria-label={tituloConfirmacion}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start gap-2">
              <AlertTriangle className="w-5 h-5 text-red-600 shrink-0 mt-0.5" />
              <h3 className="text-base font-semibold text-gray-900">{tituloConfirmacion}</h3>
            </div>
            <ul className="mt-3 space-y-2 max-h-[50vh] overflow-y-auto">
              {aprobadosConFactura.map((p) => (
                <li key={p.id} className="rounded-lg border border-red-100 bg-red-50/60 px-3 py-2">
                  <div className="text-sm font-medium text-gray-900">{numeroPaso(p.id)}. {p.titulo}</div>
                  {p.explicacion && <div className="text-xs text-gray-600 mt-0.5 whitespace-pre-wrap">{p.explicacion}</div>}
                </li>
              ))}
            </ul>
            <div className="mt-4 flex flex-col-reverse sm:flex-row sm:justify-end gap-2">
              <button
                type="button"
                onClick={() => setConfirmando(false)}
                disabled={ejecutando}
                className="min-h-[44px] sm:min-h-0 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={() => ejecutar(true)}
                disabled={ejecutando}
                data-uso="ayuda_ia.confirmar_factura"
                className="min-h-[44px] sm:min-h-0 inline-flex items-center justify-center gap-2 rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50"
              >
                {ejecutando ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                {ejecutando ? 'Ejecutando...' : 'Sí, ejecutar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AyudaIAPanel;
